import React, { useState, useCallback, useMemo, useEffect } from "react";
import { StyleSheet } from "react-native";
import TaskForm from "../../../components/tasks/TaskForm/TaskForm";
import {
  useAddTodoMutation,
  useUpdateTodoMutation,
  TodosDocument,
  RecentTodosDocument,
  useGetUserWorkspacesQuery,
} from "../../../graphql/generated/graphql";
import { DatePickerModal } from "react-native-paper-dates";
import { ScrollView } from "react-native-gesture-handler";

const TaskFormContainer = ({ task, onComplete }) => {
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [date, setDate] = useState(task && task.date ? new Date(task.date) : new Date());
  const [title, setTitle] = useState(task ? task.name : "");
  const [description, setDescription] = useState(task ? task.description : "");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(task?.workspacesHasTodo?.[0]?.id || undefined);

  useEffect(() => {
    if (task) {
      setDate(task.date ? new Date(task.date) : new Date());
      setTitle(task.name || "");
      setDescription(task.description || "");
      setSelectedWorkspaceId(task.workspacesHasTodo?.[0]?.id || undefined);
    } else {
      setDate(new Date());
      setTitle("");
      setDescription("");
      setSelectedWorkspaceId(undefined);
    }
  }, [task]);

  const { data: workspacesData } = useGetUserWorkspacesQuery();

  const workspaceOptions = useMemo(() => {
    const options = [];
    if (workspacesData?.getUserWorkspaces) {
      workspacesData.getUserWorkspaces.forEach((workspace) => {
        options.push({ label: workspace.name, value: workspace.id });
      });
    }
    return options;
  }, [workspacesData]);

  const addTodoRefetchQueries = useMemo(() => {
    return [{ query: TodosDocument }, { query: RecentTodosDocument }];
  }, []);

  const [addTodoMutation] = useAddTodoMutation({
    refetchQueries: addTodoRefetchQueries,
  });
  const [updateTodoMutation] = useUpdateTodoMutation({
    refetchQueries: [{ query: TodosDocument }, { query: RecentTodosDocument }],
  });

  const handleTitleChange = (title) => {
    setTitle(title);
  };

  const handleDescriptionChange = (description) => {
    setDescription(description);
  };

  const handleDatePick = () => {
    setIsDatePickerVisible(true);
  };

  const handleSubmit = async () => {
    if (!title || !date) {
      // TODO handle incomplete form
      return;
    }
    let endOfDayDate = new Date(date);
    endOfDayDate.setHours(23, 59, 59, 0);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (task) {
      await updateTodoMutation({
        variables: {
          id: task.id,
          todo: {
            name: title,
            timezone,
            description: description || undefined,
            date: endOfDayDate.toISOString(),
            completed: task.completed,
            completed_date: task.completed && task.completed_date ? task.completed_date : undefined,
            priority: undefined,
            isRecurring: undefined,
            recurringFrequency: undefined,
            recurringDaily: undefined,
            recurringDayOfWeek: undefined,
            recurringDayOfMonth: undefined,
            recurringTime: undefined,
            timeOfDay: undefined,
            workspaceId: selectedWorkspaceId || undefined,
          },
        },
      });
    } else {
      await addTodoMutation({
        variables: {
          todo: {
            name: title,
            description: description || undefined,
            date: endOfDayDate.toISOString(),
            timezone: timezone || undefined,
            priority: undefined,
            isRecurring: undefined,
            recurringFrequency: undefined,
            recurringDaily: undefined,
            recurringDayOfWeek: undefined,
            recurringDayOfMonth: undefined,
            recurringTime: undefined,
            timeOfDay: undefined,
            workspaceId: selectedWorkspaceId || undefined,
          },
        },
      });
    }
    onComplete();
  };

  const handleDatePickerDismiss = useCallback(() => {
    setIsDatePickerVisible(false);
  }, [setIsDatePickerVisible]);

  const handleDatePickerConfirm = useCallback(
    (params) => {
      setIsDatePickerVisible(false);
      setDate(params.date);
    },
    [isDatePickerVisible, setIsDatePickerVisible]
  );

  return (
    <>
      <ScrollView showsVerticalScrollIndicator={true} style={styles.scrollView} tabIndex={0}>
        <TaskForm
          title={title}
          description={description}
          startDate={date}
          isEdit={!!task}
          onChangeTitle={handleTitleChange}
          onChangeDescription={handleDescriptionChange}
          onDatePick={handleDatePick}
          onSubmit={handleSubmit}
          onCancel={onComplete}
          workspaceOptions={workspaceOptions}
          selectedWorkspaceId={selectedWorkspaceId}
          onWorkspaceChange={setSelectedWorkspaceId}
        />
      </ScrollView>
      <DatePickerModal
        locale="en"
        mode="single"
        label="Select date"
        visible={isDatePickerVisible}
        onDismiss={handleDatePickerDismiss}
        date={date}
        onConfirm={handleDatePickerConfirm}
        validRange={{
          startDate: new Date(),
        }}
      />
    </>
  );
};

export default TaskFormContainer;

const styles = StyleSheet.create({
  scrollView: {
    paddingHorizontal: 0,
  },
});
